import React, { useState, useEffect } from 'react'
/* global Office */
import Login from './Login'
import Dashboard from './Dashboard'
import MessageComposeView from './MessageComposeView'
import authService from '../services/authService'
import incidentCommunicationsService from '../services/incidentCommunicationsService'
import propertyService from '../services/propertyService'
import contactService from '../services/contactService'
import incidentService from '../services/incidentService'
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext'
import { useTextSize } from '../contexts/TextSizeContext'
import { detectOutlookContext } from '../utils/contextDetector'

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [contextVersion, setContextVersion] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastProcessedItemId, setLastProcessedItemId] = useState(null);
  const [outlookContext, setOutlookContext] = useState(null);
  const [socketResetFn, setSocketResetFn] = useState(null);
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  // Update Outlook context detection
  const updateOutlookContext = () => {
    const context = detectOutlookContext();
    console.log('📍 Outlook Context Detected:', context);
    setOutlookContext(context);
    return context;
  };

  // Manual refresh handler
  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    console.log('🔄 Manual refresh triggered');
    
    try {
      // Force clear cache to ensure fresh data
      incidentCommunicationsService.clearCache();
      
      // Reset socket connection to fix "Connecting..." state
      if (socketResetFn) {
        console.log('🔄 Resetting socket connection...');
        socketResetFn();
      }
      
      await updateContextFromIncidentCommunications(true); // Pass true to force refresh
    } finally {
      setIsRefreshing(false);
    }
  };

  // Callback to receive socket reset function from AIAssistant
  const handleSocketResetReady = (resetFn) => {
    setSocketResetFn(() => resetFn);
  };

  // Centralized routine to pull IDs from the current Outlook item/message
  const updateContextFromIncidentCommunications = async (forceRefresh = false) => {
    // Get current item ID to track if we've already processed this email
    const currentItemId = Office?.context?.mailbox?.item?.itemId;

    // Re-initialize the message ID and thread ID from the current item (important for ItemChanged)
    incidentCommunicationsService.initializeMessageId();
    incidentCommunicationsService.initializeThreadId();

    // Skip duplicate fetches when the same item fires ItemChanged repeatedly
    if (
      !forceRefresh &&
      currentItemId &&
      currentItemId === lastProcessedItemId &&
      incidentCommunicationsService.isMessageIdAvailable()
    ) {
      console.log('⏭️ Skipping incident communications fetch - item already processed:', currentItemId);
      return;
    }

    try {
      const shouldClearCache =
        forceRefresh ||
        !lastProcessedItemId ||
        lastProcessedItemId !== currentItemId;

      if (shouldClearCache) {
        incidentCommunicationsService.clearCache();
        if (forceRefresh) {
          console.log('🔄 Force refresh - cache cleared');
        } else if (lastProcessedItemId) {
          console.log('🆕 New Outlook item detected - cache cleared');
        } else {
          console.log('🆕 Initial Outlook item detected - cache cleared');
        }
      } else {
        console.log('♻️ Reusing cached incident communications data for current item');
      }

      const messageData = await incidentCommunicationsService.getThreadData(forceRefresh);
      const refetchInfo = incidentCommunicationsService.getRefetchInfo();
      
      console.log('🔄 Update Context - Refetch Info:', refetchInfo);
      
      // Track the last processed item ID
      setLastProcessedItemId(currentItemId);

      // Handle "not found" case gracefully
      if (messageData.notFound) {
        console.warn('⚠️ Incident communication index not found - clearing IDs, AI Assistant will work in fallback mode');
        propertyService.setPropertyId(null);
        incidentService.setIncidentId(null);
        contactService.setContactId(null);
        return; // Exit early, AI Assistant will use Office.js thread ID
      }

      // Extract IDs from message data
      const propertyId = incidentCommunicationsService.extractPropertyId(messageData);
      const contactId = incidentCommunicationsService.extractContactId(messageData);
      const incidentId = incidentCommunicationsService.extractIncidentId(messageData);

      // Update IDs based on refetch strategy
      if (refetchInfo.needsFullRefetch) {
        // Different thread - update everything
        console.log('🔀 DIFFERENT THREAD - Updating all IDs');
        
        if (propertyId) {
          propertyService.setPropertyId(propertyId);
          console.log('✅ Property ID set:', propertyId);
        } else {
          propertyService.setPropertyId(null);
          console.log('❌ No property ID found - cleared');
        }

        if (incidentId) {
          incidentService.setIncidentId(incidentId);
          console.log('✅ Incident ID set:', incidentId);
        } else {
          incidentService.setIncidentId(null);
          console.log('❌ No incident ID found - cleared');
        }

        if (contactId) {
          contactService.setContactId(contactId);
          console.log('✅ Contact ID set:', contactId);
        } else {
          contactService.setContactId(null);
          console.log('❌ No contact ID found - cleared');
        }
      } else if (refetchInfo.needsContactRefetch) {
        // Same thread, different message - only update contact ID
        console.log('✅ SAME THREAD - Only updating Contact ID');
        
        if (contactId) {
          contactService.setContactId(contactId);
          console.log('✅ Contact ID updated:', contactId);
        } else {
          contactService.setContactId(null);
          console.log('❌ No contact ID found - cleared');
        }
        
        // Property and Incident IDs remain the same (don't update)
        console.log('ℹ️ Property ID and Incident ID unchanged (same thread)');
      } else {
        // No changes needed
        console.log('✅ No changes needed - using cached data');
      }
    } catch (error) {
      console.error('❌ Failed to fetch incident communications:', error);
      // Clear all IDs on error to avoid showing stale data
      propertyService.setPropertyId(null);
      contactService.setContactId(null);
      incidentService.setIncidentId(null);
    } finally {
      // Bump a version so dependent components can refetch on context change
      setContextVersion((v) => v + 1);
    }
  };

  useEffect(() => {
    // Check if user is already authenticated on app start
    const checkAuthStatus = async () => {
      // Detect Outlook context first
      updateOutlookContext();
      
      if (authService.isAuthenticated()) {
        setIsLoggedIn(true);
        setUser(authService.getCurrentUser());
        
        // Only fetch incident communications if we're in MESSAGE_READ context
        const context = updateOutlookContext();
        if (context.type === 'MESSAGE_READ') {
          await updateContextFromIncidentCommunications();
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();

    // Listen for logout events (e.g., when token expires)
    const handleLogout = () => {
      setIsLoggedIn(false);
      setUser(null);
    };

    window.addEventListener('auth:logout', handleLogout);

    console.log(Office?.context?.mailbox?.item);
    
    // When pinned, the task pane stays and ItemChanged fires on item switch
    // Register handler only once on mount
    const handleItemChanged = async () => {
      try {
        const currentItem = Office.context.mailbox.item;
        console.log('📧 ItemChanged event fired - New email opened!');
        console.log('  Subject:', currentItem?.subject);
        console.log('  Conversation ID:', currentItem?.conversationId);
        console.log('  Item Type:', currentItem?.itemType);
        console.log('  Date/Time:', new Date().toLocaleTimeString());
        
        // Update context detection
        const context = updateOutlookContext();
        
        // Check auth status directly (not from closure) to avoid stale state
        if (authService.isAuthenticated()) {
          console.log('  ✅ User authenticated');
          
          // Only fetch incident communications if we're in MESSAGE_READ context
          if (context.type === 'MESSAGE_READ') {
            console.log('  📖 Message Read context - fetching data...');
            await updateContextFromIncidentCommunications();
          } else {
            console.log('  ℹ️ Not in Message Read context - skipping incident communications fetch');
          }
        } else {
          console.log('  ⚠️ User not logged in, skipping data fetch');
        }
      } catch (e) {
        console.error('Error handling ItemChanged:', e);
      }
    };

    if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox) {
      try {
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, handleItemChanged);
      } catch (e) {
        console.error('Failed to register ItemChanged handler:', e);
      }
    }

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox) {
        try {
          // Best-effort cleanup
          Office.context.mailbox.removeHandlerAsync(
            Office.EventType.ItemChanged,
            { handler: handleItemChanged }
          );
        } catch (e) {
          // ignore
        }
      }
    };

  }, []);

  const handleLogin = async (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    
    // Only fetch incident communications if we're in MESSAGE_READ context
    const context = updateOutlookContext();
    if (context.type === 'MESSAGE_READ') {
      await updateContextFromIncidentCommunications();
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsLoggedIn(false);
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={`${currentTextSize.bodyText} text-gray-600`}>{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />
  }

  // Route to appropriate view based on Outlook context
  console.log('🎯 Routing based on context:', outlookContext?.type);
  
  if (outlookContext) {
    switch (outlookContext.type) {
      case 'MESSAGE_READ':
        console.log('  ➡️ Rendering Dashboard (MESSAGE_READ)');
        // Existing functionality - show the full Dashboard with property/contact/incident info
        return <Dashboard 
          user={user} 
          onLogout={handleLogout} 
          onRefresh={handleManualRefresh}
          isRefreshing={isRefreshing}
          contextVersion={contextVersion}
          onSocketResetReady={handleSocketResetReady}
        />
      
      case 'MESSAGE_COMPOSE':
        console.log('  ➡️ Rendering MessageComposeView (MESSAGE_COMPOSE)');
        // Show compose view with AI assistant, templates, etc.
        // This is also where "inbox-level" features can live (write to property, etc.)
        return <MessageComposeView 
          user={user} 
          onLogout={handleLogout} 
        />
      
      case 'MODULE':
        // Module context detected but not supported on Desktop Outlook
        // Fall back to Dashboard if somehow triggered
        console.log('  ⚠️ MODULE context detected (unsupported on Desktop) - falling back to Dashboard');
        return <Dashboard 
          user={user} 
          onLogout={handleLogout} 
          onRefresh={handleManualRefresh}
          isRefreshing={isRefreshing}
          contextVersion={contextVersion}
          onSocketResetReady={handleSocketResetReady}
        />
      
      default:
        console.log('  ➡️ Rendering Dashboard (default fallback for:', outlookContext.type, ')');
        // Fallback to Dashboard for unknown contexts
        return <Dashboard 
          user={user} 
          onLogout={handleLogout} 
          onRefresh={handleManualRefresh}
          isRefreshing={isRefreshing}
          contextVersion={contextVersion}
          onSocketResetReady={handleSocketResetReady}
        />
    }
  }

  // Fallback if context hasn't been detected yet
  return <Dashboard 
    user={user} 
    onLogout={handleLogout} 
    onRefresh={handleManualRefresh}
    isRefreshing={isRefreshing}
    contextVersion={contextVersion}
    onSocketResetReady={handleSocketResetReady}
  />
}

export default function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}
